/**
 * Componente para listar os usuários, pode ser usado na página de listagem de usuários
 * Ele busca os usuários do backend usando o token de autenticação armazenado no LocalStorage e exibe a lista na tela.
 * Se ocorrer algum erro durante a busca, ele redireciona para a página de resultado com a mensagem de erro.    
 * O componente usa o hook useEffect para buscar os usuários assim que for montado, e o hook useState para armazenar a lista de usuários no estado do componente.
 * O hook useNavigate é usado para redirecionar o usuário para a página de resultado em caso de erro.   
 * A URL da requisição para buscar os usuários é construída usando a variável de ambiente REACT_APP_API_URL, garantindo que a aplicação possa ser configurada para diferentes ambientes (desenvolvimento, produção, etc.) sem precisar alterar o código.
 *  Estilo básico usando classes do Bootstrap para a lista de usuários.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contextos/AuthContext"; // Importa o hook useAuth do contexto de autenticação para acessar o estado do usuário logado e funções relacionadas à autenticação
import Botao from "../Botao";
import fotoIndisponivel from "../../assets/imagens/indisponivel.png";
import CampoTexto from "../CampoTexto";
import './ListaUsuarios.css'; // Importa o arquivo CSS para estilizar o componente ListaUsuarios
import { 
  Search, 
  ShieldCheck, 
  ShieldAlert, 
  UserX, 
  UserCheck, 
  Calendar, 
  Key, 
  Mail, 
  User 
} from "lucide-react";

//Componente para listar os usuários, pode ser usado na página de listagem de usuários
const ListaUsuarios = () => {

const { token } = useAuth(); // reage automaticamente ao login!
const navigate = useNavigate(); // Hook para navegação programática 
const [usuarios, setUsuarios] = useState([]); // Estado para armazenar a lista de usuários, inicialmente vazio
const [usuariosFiltrados, setUsuariosFiltrados] = useState([]); // Estado para armazenar a lista de usuários filtrados, inicialmente vazio
const [termoBusca, setTermoBusca] = useState(''); // Estado para armazenar o termo de busca digitado pelo usuário


/* Esta função roda uma vez quando o componente é montado
/* Carrega a lista de usuários do backend assim que o componente for exibido, garantindo que tenhamos os dados para mostrar na tela */    
useEffect(() => {
    //Criamos uma função assíncrona para buscar os usuários do backend, incluindo o token de autenticação no cabeçalho da requisição. Tem que ser criada dentro do useEffect porque o useEffect não pode ser assíncrono diretamente, mas pode chamar funções assíncronas.
    const buscarUsuarios = async () => {
        
        if (!token) {
            navigate('/resultado', { 
                    state: { 
                    status: "erro",
                    mensagem: "Token de autenticação não encontrado. O usuário pode não estar logado."
                          }
                    });   
                    return; // Encerra a função se o token não for encontrado 
        }

        try{
            // Faz a requisição para o backend para buscar os usuários, incluindo o token no cabeçalho de autorização
            
            //Usa a URL da sua variável de ambiente
            const url = process.env.REACT_APP_API_URL + '/user-list';

            const resposta = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    // 3. Envia o token via Header Authorization
                    'Authorization': `Bearer ${token}`
                }
            });


            if (!resposta.ok) {
                    // Se o backend retornar 401 ou 403, algo está errado com a permissão
                    throw new Error(`Erro na requisição: ${resposta.status}`);
                }
            
            const dadosRetorno = await resposta.json();
        
            if (dadosRetorno.status === 'sucesso') {
            // Em React, em vez de chamar uma função 'listarUsuarios', 
            // nós salvamos os dados no estado (useState)

            setUsuarios(dadosRetorno.dados); // Salva os usuários no estado paratermos os dados originalmente recebidos do backend disponíveis para renderização e outras operações
            setUsuariosFiltrados(dadosRetorno.dados); // Inicialmente, a lista filtrada é a mesma que a lista completa, mas manteremos essa separação para facilitar a implementação da funcionalidade de busca/filtro posteriormente
            

            } else {
                navigate('/resultado', { 
                    state: { 
                    status: dadosRetorno.status,
                    mensagem: dadosRetorno.mensagem
                          }
                    });   
            }
        }catch(error){
            navigate('/resultado', { 
                    state: { 
                    status: "erro",
                    mensagem: "Ocorreu um erro ao buscar os usuários: " + error.message
                          }
                    });
        }
    }
    buscarUsuarios(); // Chama a função para buscar os usuários quando o componente for montado
   
}, []);

//Recebe os dados do usuário e o novo status para atualizar o status do usuário, fazendo uma requisição para o backend. O mesmo para o tipo de usuário. Essas funções ainda precisam ser implementadas, mas a ideia é que elas façam uma requisição PUT ou PATCH para o backend, enviando o ID do usuário e o novo status ou tipo, e depois atualizem a lista de usuários na tela com os novos dados retornados pelo backend.
const upadateStatusUsuario = async (usuario, novoStatus) => {
    //Conforme for o status do usuário atual, a função deve enviar a requisição para o backend para bloquear ou desbloquear o usuário, e depois atualizar a lista de usuários na tela com os novos dados retornados pelo backend.

    //Busca o token do LocalStorage para autenticação
    //const token = localStorage.getItem('viver_token');
    

    if (!token) {
        navigate('/resultado', { 
                state: { 
                status: "erro",
                mensagem: "Token de autenticação não encontrado. O usuário pode não estar logado."
                      }
                });   
                return; // Encerra a função se o token não for encontrado 
    }

    try{
        //Faz a requisição para o backend para atualizar o status do usuário, incluindo o token no cabeçalho de autorização
        
        //Usa a URL da sua variável de ambiente
        
        const url = process.env.REACT_APP_API_URL + '/user-update-status'; // Supondo que o backend tenha uma rota para atualizar o status do usuário, usando o ID ou nome de usuário como parâmetro


        const resposta = await fetch(url, {
            method: 'PATCH', // utilizado no padrão REST para atualizações parciais, mas pode ser PUT dependendo de como o backend foi implementado
            headers: {
                'Content-Type': 'application/json',
                // Envia o token via Header Authorization
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ 
                user: usuario.usuario, // Envia o nome de usuário ou ID para identificar qual usuário atualizar
                status: novoStatus }) // Envia o novo status no corpo da requisição
        });

        if (!resposta.ok) {
                // Se o backend retornar 401 ou 403, algo está errado com a permissão
                throw new Error(`Erro na requisição: ${resposta.status}`);
            }
        
        const dadosRetorno = await resposta.json();

        if (dadosRetorno.status === 'sucesso') {
            navigate('/resultado', { 
                state: { 
                status: dadosRetorno.status,
                mensagem: dadosRetorno.mensagem
                      }
                });   


        } else {
            navigate('/resultado', { 
                state: { 
                status: dadosRetorno.status,
                mensagem: dadosRetorno.mensagem
                      }
                });   
        }
    }catch(error){
        navigate('/resultado', { 
                state: { 
                status: "erro",
                mensagem: "Ocorreu um erro ao atualizar o status do usuário: " + error.message
                      }
                });
    }


}


//Recebe os dados do usuário para atualizar o tipo do usuário, fazendo uma requisição para o backend. O mesmo para o status do usuário. Essas funções ainda precisam ser implementadas, mas a ideia é que elas façam uma requisição PUT ou PATCH para o backend, enviando o ID do usuário e o novo status ou tipo, e depois atualizem a lista de usuários na tela com os novos dados retornados pelo backend.
const updateTipoUsuario = async (usuario) => {
    //Conforme for o tipo de usuário atual, a função deve enviar a requisição para o backend para promover ou rebaixar o usuário, e depois atualizar a lista de usuários na tela com os novos dados retornados pelo backend.
    
    //Busca o token do LocalStorage para autenticação
    //const token = localStorage.getItem('viver_token');

    if (!token) {
        navigate('/resultado', { 
                state: { 
                status: "erro",
                mensagem: "Token de autenticação não encontrado. O usuário pode não estar logado."
                      }
                });   
                return; // Encerra a função se o token não for encontrado 
    }

    try{
        //Faz a requisição para o backend para atualizar o status do usuário, incluindo o token no cabeçalho de autorização
        
        //Usa a URL da sua variável de ambiente


        //Neste ponto analisa o tipo atual do usuário para decidir qual será o novo tipo a ser enviado para o backend. Por exemplo, se o tipo atual for 'comum', o novo tipo pode ser 'admin', e se for 'admin', o novo tipo pode ser 'comum'. A lógica exata vai depender dos tipos de usuário que você tem no seu sistema e de como você quer que a promoção/rebaixamento funcione.
        const novoTipo = usuario.tipo === 'leitor' 
                                        ? 'editor' 
                                        : usuario.tipo === 'editor' 
                                        ? 'admin' : 'leitor'; // Exemplo de lógica para alternar entre 'comum' e 'admin', ajuste conforme seus tipos de usuário
        
        const url = process.env.REACT_APP_API_URL + '/user-update-tipo'; // Supondo que o backend tenha uma rota para atualizar o tipo do usuário, usando o ID ou nome de usuário como parâmetro


        const resposta = await fetch(url, {
            method: 'PATCH', // utilizado no padrão REST para atualizações parciais, mas pode ser PUT dependendo de como o backend foi implementado
            headers: {
                'Content-Type': 'application/json',
                // Envia o token via Header Authorization
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ 
                user: usuario.usuario, // Envia o nome de usuário ou ID para identificar qual usuário atualizar
                tipo: novoTipo }) // Envia o novo tipo no corpo da requisição
        });

        if (!resposta.ok) {
                // Se o backend retornar 401 ou 403, algo está errado com a permissão
                throw new Error(`Erro na requisição: ${resposta.status}`);
            }
        
        const dadosRetorno = await resposta.json();

        if (dadosRetorno.status === 'sucesso') {
            navigate('/resultado', { 
                state: { 
                status: dadosRetorno.status,
                mensagem: dadosRetorno.mensagem
                      }
                });   


        } else {
            navigate('/resultado', { 
                state: { 
                status: dadosRetorno.status,
                mensagem: dadosRetorno.mensagem
                      }
                });   
        }
    }catch(error){
        navigate('/resultado', { 
                state: { 
                status: "erro",
                mensagem: "Ocorreu um erro ao atualizar o tipo do usuário: " + error.message
                      }
                });
    }
}


/* Função para filtrar os usuários com base no termo de busca digitado pelo usuário, atualizando a lista de usuários exibida na tela conforme o termo de busca é digitado. A função verifica se o termo de busca está presente no nome ou no nome de usuário dos usuários, e atualiza a lista filtrada de acordo. Se o campo de busca for limpo, a lista original de usuários é restaurada. */
const filtrarUsuarios = (termo) => {
    setTermoBusca(termo); // Atualiza o estado do termo de busca para refletir o valor digitado pelo usuário no campo de busca
    if (termo.trim() === "") {
        setUsuariosFiltrados(usuarios); // Se limpar, volta a lista original
        return;
    }
    // Filtra os usuários com base no termo de busca, verificando se o termo está presente no nome ou no nome de usuário (case-insensitive)
    const resultado = usuarios.filter((usuario) => {
        return (
            usuario.nome.toLowerCase().includes(termo.toLowerCase()) ||
            usuario.usuario.toLowerCase().includes(termo.toLowerCase())
        );
    });

    setUsuariosFiltrados(resultado); // AQUI é onde a mágica acontece na tela  
}
    

// Helper para rótulo do botão de status
const getTextoBotaoStatus = (status) => {
    if (status === 'ativo') return 'Bloquear';
    if (status === 'cadastrado') return 'Aprovar';
    if (status === 'resetsenha') return 'Bloquear';
    return 'Aprovar';
};

// Helper para rótulo do botão de tipo
const getTextoBotaoTipo = (tipo) => {
    if (tipo === 'leitor') return 'Tornar Editor';
    if (tipo === 'editor') return 'Tornar Admin';
    return 'Tornar Leitor';
};

if (usuarios.length === 0) {
        return (
            <div className="container-usuarios-vazio">
                <h2>Gestão de Usuários</h2>
                <p >Nenhum usuário encontrado no sistema.</p>
            </div>
        );
    }
 
    return (
        <div className="container-lista-usuarios">
            {/* Cabeçalho da Seção */}
            <div className="header-lista-usuarios">
                <h1 className="titulo-lista-usuarios">Gestão de Usuários</h1>
                <p className="subtitulo-lista-usuarios">Gerencie os acessos e permissões dos usuários do sistema.</p>            
            </div>

            {/* Caixa de Busca com Ícone Integrado */}
            <div className="caixa-busca-usuarios">
                <Search className="icone-lupa" size={18} />
                <CampoTexto
                    label={''}
                    wrapperClass={'w-100'}
                    className="input-busca-usuarios"
                    placeholder="Digite o nome ou e-mail/usuário para buscar..."
                    valor={termoBusca} // Você pode implementar a lógica de busca e armazenar o valor do campo de busca no estado, usando useState, e passar esse valor aqui para controlar o campo de texto
                    aoDigitado={(valor) => filtrarUsuarios(valor)} // Você pode implementar a lógica de busca e passar uma função aqui que atualize o estado com o valor do campo de busca, e depois use esse valor para filtrar a lista de usuários exibida na tela
                    editavel={true}
                >
                 </CampoTexto>        
            </div>
            

            {/* Lista de Cards de Usuários */}
            <div className="grid-cards-usuarios">
                {usuariosFiltrados.map((usuario) => (
                
                <div key={usuario.usuario} className="card-usuario-item">
                    {/* Topo do Card: Foto, Nome e Badges */}
                     
                    <div className="card-usuario-header">
                        <img 
                            src={usuario.foto ? usuario.foto : fotoIndisponivel} 
                            alt="Foto do usuário" 
                            className="avatar-usuario"
                        />
              
                        <div className="info-principal-usuario">
                            <div className="nome-badges-row">
                                <h3 className="nome-usuario-txt">{usuario.nome || usuario.usuario}</h3>
                                    <div className="group-badges">
                                        {/* Badge Tipo */}
                                        <span className={`badge-tag badge-tipo-${usuario.tipo}`}>
                                            {usuario.tipo === 'admin' && <ShieldCheck size={12} />}
                                            {usuario.tipo}
                                        </span>
                                        {/* Badge Status */}
                                        <span className={`badge-tag badge-status-${usuario.status}`}>
                                            {usuario.status}
                                        </span>
                                    </div>
                            </div>

                            {/* E-mail / Usuário */}
                            <div className="dado-linha">
                                <Mail size={14} className="icone-dado" />
                                <span>{usuario.usuario}</span>
                            </div>
                        </div>
                    </div>
                        {/* Dados Secundários (Código Ativação + Último Acesso) */}
                        <div className="card-usuario-detalhes">
                            {usuario.codigoAtivacao && (
                                <div className="dado-linha">
                                    <Key size={13} className="icone-dado" />
                                    <span>Código ativação: <strong>{usuario.codigoAtivacao}</strong></span>
                                </div>
                        )}

                            {usuario.ultimoAcesso && (
                                <div className="dado-linha">
                                <Calendar size={13} className="icone-dado" />
                                <span>Último Acesso: {usuario.ultimoAcesso}</span>
                            </div>
                        )}
                        </div>

                        {/* Ações / Botões no Rodapé do Card */}    
                    
                        <div className="card-usuario-acoes">
                            <Botao 
                                className={`btn-acao-usr ${usuario.status === 'ativo' ? 'btn-danger-usr' : 'btn-success-usr'}`}
                                tipo={'button'}
                                aoClicar={() => upadateStatusUsuario(usuario, ['bloqueado', 'cadastrado'].includes(usuario.status) ? 'ativo' : 'bloqueado')} > {/* Se o status for 'ativo', mostra 'Bloquear', senão mostra 'Aprovar'. Envia o novo status de acordo com o status atual. Se estiver como bloqueado ou cadastrado envia ativo. Senão envia bloqueado */}
                                {usuario.status === 'ativo' ? <UserX size={15} /> : <UserCheck size={15} />}
                                <span>{getTextoBotaoStatus(usuario.status)}</span>
                            </Botao>
                        </div>    
                            
                            <div className="d-flex p-2 flex-fill">
                                <Botao 
                                    className={"btn-acao-usr btn-secondary-usr"}    
                                    tipo={'button'}
                                    aoClicar={() => updateTipoUsuario(usuario)} > {/* Exemplo de botão para editar o usuário, redirecionando para uma página de edição (a ser implementada) */}
                                    <ShieldAlert size={15} />
                                    <span>{getTextoBotaoTipo(usuario.tipo)}</span>
                                </Botao>
                            </div>   
                </div>
                ))}
            </div>
        </div>
    )
}

export default ListaUsuarios;